import { useState } from "react";
import { PhoneNav } from "../../shared/phone/PhoneNav";
import { Sheet } from "../../shared/phone/Sheet";
import type { PartnerReferralsView, ReferralDTO, ReferralStage } from "../../shared/types";
import { count, money, shortDate } from "../lib/format";
import { Icon } from "../components/phone/Icon";
import { EmptyBlock, Monogram, PoweredBy, RangeButton } from "../components/phone/parts";

/** The dates the list shows: a preset, or two days the partner picked. */
export type ReferralRange =
  | { kind: "today" | "week" | "7" | "30"; label: string }
  | { kind: "custom"; label: string; from: string; to: string };
export type ReferralPreset = { kind: "today" | "week" | "7" | "30"; label: string };

const STAGE: Record<ReferralStage, string> = {
  customer: "Customer",
  onhold: "On hold",
  trial: "Trial",
  signup: "Signed up",
  churned: "Churned",
  click: "Clicked",
};

function earned(r: ReferralDTO, currency: string): { text: string; cls: string } {
  if (r.reversed) return { text: "reversed", cls: "po-amt dim" };
  if (r.earnedCents > 0) return { text: money(r.earnedCents, currency), cls: r.cleared ? "po-amt good" : "po-amt" };
  return { text: "pending", cls: "po-amt dim" };
}

const two = (n: number) => String(n).padStart(2, "0");

/** A local day as "YYYY-MM-DD", for a date field. */
function isoDay(d: Date): string {
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

/**
 * Referrals on a phone and an iPad: the three counts in one compact row, then each person the partner
 * brought in as a row, with when they joined, their stage and what the partner earned. The dates open a
 * sheet with presets and the phone's own date picker.
 */
export function ReferralsNative({
  view,
  error,
  onRetry,
  range,
  presets,
  rows,
  onPreset,
  onCustom,
}: {
  view: PartnerReferralsView | null;
  error: string | null;
  onRetry: () => void;
  range: ReferralRange;
  presets: ReferralPreset[];
  rows: ReferralDTO[];
  onPreset: (p: ReferralPreset) => void;
  onCustom: (from: string, to: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const today = isoDay(new Date());
  const [from, setFrom] = useState(() => (range.kind === "custom" ? range.from : isoDay(new Date(Date.now() - 29 * 86_400_000))));
  const [to, setTo] = useState(() => (range.kind === "custom" ? range.to : today));
  const label = range.kind === "custom" ? `${shortDate(range.from)} to ${shortDate(range.to)}` : range.label;

  let body;
  if (error && !view) {
    body = (
      <div className="pn-stack">
        <EmptyBlock icon="info" title="Something went wrong">
          {error}
        </EmptyBlock>
        <div className="pn-pad">
          <button type="button" className="pn-btn soft wide" onClick={onRetry}>
            Try again
          </button>
        </div>
      </div>
    );
  } else if (!view) {
    body = <div className="po-loading" role="status" aria-label="Loading" />;
  } else if (view.referrals.length === 0) {
    body = (
      <div className="pn-stack">
        <EmptyBlock icon="people" title="No referrals yet">
          When someone taps your link and signs up, they show up here with their stage and what you earned.
        </EmptyBlock>
        <PoweredBy />
      </div>
    );
  } else {
    body = (
      <div className="pn-stack">
        <section className="pn-card po-counts">
          <div>
            <span className="n">{count(view.kpis.clicks)}</span>
            <span>Clicked</span>
          </div>
          <div>
            <span className="n">{count(view.kpis.signups)}</span>
            <span>Signed up</span>
          </div>
          <div>
            <span className="n">{count(view.kpis.customers)}</span>
            <span>Customers</span>
          </div>
        </section>
        <section>
          <div className="pn-sh po-sh">
            <h2>People you brought in</h2>
            <RangeButton label={label} onClick={() => setPicking(true)} />
          </div>
          {rows.length ? (
            <div className="pn-grp">
              {rows.map((r) => {
                const e = earned(r, view.currency);
                const name = r.personLabel ?? "New referral";
                return (
                  <div className="pn-row static" key={r.id}>
                    <Monogram name={name} />
                    <span className="m">
                      <span className="t">{name}</span>
                      <span className="s">
                        Joined {shortDate(r.joinedAt)}, {STAGE[r.stage]}
                      </span>
                    </span>
                    <span className={`v ${e.cls}`}>{e.text}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="po-none">No one joined in these days.</p>
          )}
        </section>
        <PoweredBy />
      </div>
    );
  }

  return (
    <div className="pn-screen">
      <PhoneNav large title="Referrals" />
      {body}
      <Sheet
        open={picking}
        onClose={() => setPicking(false)}
        title="Show people who joined"
        foot={
          <button
            type="button"
            className="pn-btn pri wide"
            disabled={!from || !to}
            onClick={() => {
              setPicking(false);
              onCustom(from, to);
            }}
          >
            Show these days
          </button>
        }
      >
        <div className="pn-grp">
          {presets.map((p) => (
            <button
              key={p.kind}
              type="button"
              className="pn-row"
              onClick={() => {
                setPicking(false);
                onPreset(p);
              }}
            >
              <span className="m">
                <span className="t">{p.label}</span>
              </span>
              {range.kind === p.kind ? <Icon name="check" className="po-tick" /> : null}
            </button>
          ))}
        </div>
        <div className="pn-cap po-cap2">Or pick the days</div>
        <div className="pn-grp">
          <div className="pn-irow">
            <label htmlFor="po-rf">From</label>
            <input id="po-rf" type="date" value={from} max={today} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="pn-irow">
            <label htmlFor="po-rt">To</label>
            <input id="po-rt" type="date" value={to} max={today} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      </Sheet>
    </div>
  );
}
